import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../utils/constants';
import './AuthPage.css';

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [identifier, setIdentifier] = useState(''); // email or username
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const { login, resetPassword, loading, error, clearError } = useAuth();
  const { isAuthenticated, needsRegistration } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (needsRegistration) {
        navigate(ROUTES.REGISTER, { replace: true });
      } else {
        const from = location.state?.from?.pathname || ROUTES.HOME;
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, needsRegistration, navigate, location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    await login(identifier, password);
    // Auth state listener will handle redirect
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const success = await resetPassword(resetEmail);
    if (success) {
      setResetSent(true);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    clearError();
    setResetSent(false);
  };

  // Forgot Password Mode
  if (mode === 'forgot') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <span className="auth-icon">🔑</span>
            <h1>Reset Password</h1>
            <p>Enter your email to receive a password reset link</p>
          </div>

          {resetSent ? (
            <div className="reset-success">
              <span className="success-icon">✅</span>
              <p>Check your email for a password reset link.</p>
              <p className="form-hint">
                If you don't see it, check your spam folder.
              </p>
              <button
                className="btn btn-primary btn-full"
                onClick={() => switchMode('login')}
                style={{ marginTop: '1rem' }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="resetEmail">Email</label>
                <input
                  id="resetEmail"
                  type="email"
                  placeholder="john@example.com"
                  value={resetEmail}
                  onChange={(e) => {
                    setResetEmail(e.target.value);
                    clearError();
                  }}
                  required
                  autoFocus
                />
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading || !resetEmail}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

        </div>
      </div>
    );
  }

  // Login Mode (default)
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon">🔥</span>
          <h1>Streak for Cash</h1>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleLogin} className="auth-form">
          <div className="form-group">
            <label htmlFor="identifier">Username or Email</label>
            <input
              id="identifier"
              type="text"
              placeholder="SportsFan42 or john@example.com"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                clearError();
              }}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearError();
              }}
              required
            />
            <button
              type="button"
              className="btn-link forgot-link"
              onClick={() => switchMode('forgot')}
            >
              Forgot password?
            </button>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading || !identifier || !password}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-switch">
          Don't have an account? <Link to={ROUTES.REGISTER}>Sign Up</Link>
        </div>
      </div>
    </div>
  );
}
